import { buildMaintenanceProfile, type MaintenanceContext } from "@/lib/maintenance";

export interface MaintenanceYearRow {
  year: number;
  powertrain: string;
  oilIntervalMiles: number;
  rotationMiles: number;
  transmission: string | null;
  /** Services that changed versus the previous (older) model year. */
  changeNote: string | null;
  /** Vehicle configurations recorded for the year — the sitemap data floor. */
  rowCount: number;
}


/**
 * Per-model-year interval rows for the maintenance hub. The page ranks for
 * generic "<model> maintenance schedule" queries, but the real demand is
 * year-qualified ("2019 Honda Civic maintenance schedule"), so the existing
 * URL answers every year instead of one.
 */
/**
 * The profile generator reads the first row it is given, so a year whose row
 * order happens to start with a niche trim would report that trim's powertrain
 * as the whole year's schedule. Lead with the year's dominant powertrain
 * instead, so year-over-year differences reflect the model, not row order.
 */
function representativeRows(yv: any[]): any[] {
  if (yv.length < 2) return yv;
  const key = (v: any) =>
    `${v.fuel_type ?? ""}|${v.atv_type ?? ""}|${v.displacement ?? ""}`.toLowerCase();
  const counts = new Map<string, number>();
  for (const v of yv) counts.set(key(v), (counts.get(key(v)) ?? 0) + 1);
  const dominant = [...counts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  )[0]?.[0];
  const lead = yv.filter((v) => key(v) === dominant);
  return [...lead, ...yv.filter((v) => key(v) !== dominant)];
}

export function buildMaintenanceYearRows(
  vehicles: any[],
  ctx: MaintenanceContext = {},
): MaintenanceYearRow[] {
  const years = Array.from(
    new Set(vehicles.map((v) => v.year).filter((y): y is number => typeof y === "number")),
  ).sort((a, b) => b - a);

  const rows = years.map((year) => {
    const yv = representativeRows(vehicles.filter((v) => v.year === year));
    const p = buildMaintenanceProfile(yv, ctx);
    const rotation = p.items.find((i) => i.service === "Tire rotation");
    const trans = yv.find((v) => v.transmission)?.transmission ?? null;
    return {
      year,
      powertrain: p.powertrain,
      oilIntervalMiles: p.oilIntervalMiles,
      rotationMiles: rotation?.intervalMiles ?? 7500,
      transmission: trans,
      changeNote: null as string | null,
      rowCount: yv.length,
    };

  });

  // Flag the years where the schedule actually moved — that is the part a
  // year-qualified searcher is looking for.
  for (let i = 0; i < rows.length - 1; i++) {
    const cur = rows[i];
    const older = rows[i + 1];
    if (!cur || !older) continue;
    const diffs: string[] = [];
    if (cur.oilIntervalMiles !== older.oilIntervalMiles)
      diffs.push(
        `oil interval ${older.oilIntervalMiles < cur.oilIntervalMiles ? "up" : "down"} from ${older.oilIntervalMiles.toLocaleString()} mi`,
      );
    if (cur.powertrain !== older.powertrain) diffs.push(`powertrain now ${cur.powertrain}`);
    cur.changeNote = diffs.length ? diffs.join("; ") : null;
  }

  return rows;
}

/** Oldest model year worth a sitemap slot — older data is sparse and stale. */
export const MAINTENANCE_YEAR_MIN = 2015;
/** A year needs at least this many recorded configurations to earn a URL. */
export const MAINTENANCE_YEAR_MIN_ROWS = 2;

/**
 * The model year the bare /maintenance-schedule URL renders by default (the
 * newest year on record). ?year=<this> is an exact duplicate of the base page.
 */
export function defaultMaintenanceYear(rows: MaintenanceYearRow[]): number | null {
  return rows[0]?.year ?? null;
}

/**
 * Same discipline as the differentiator floor on every other page type: a
 * ?year= URL is sitemapped only when it says something the neighbouring years
 * do not. Qualifying years are the newest year (the default answer) and any
 * year whose schedule actually moved versus the year before it. Years that
 * merely repeat an older schedule stay crawlable but out of the sitemap.
 */
export function sitemapWorthyYears(rows: MaintenanceYearRow[]): number[] {
  // Future model years have EPA spec data but zero real-world service history,
  // so their rules-engine intervals have nothing to converge on. The pages stay
  // reachable; they just don't compete in search until the year is current.
  const maxYear = new Date().getFullYear();
  // The base URL already renders the newest year, so ?year=<newest> is a byte
  // duplicate of it. It gets a canonical back to the base URL and never a
  // sitemap slot or an internal link of its own.
  const defaultYear = defaultMaintenanceYear(rows);
  const eligible = rows.filter(
    (r) =>
      r.year >= MAINTENANCE_YEAR_MIN &&
      r.year <= maxYear &&
      r.year !== defaultYear &&
      r.rowCount >= MAINTENANCE_YEAR_MIN_ROWS,
  );
  return eligible
    .filter((r) => r.changeNote !== null)
    .map((r) => r.year);

}

